/**
 * Date utility functions for the Historical Timeline.
 */

/**
 * Format a year for display: negative → "X BC", positive → "AD X"
 * @param {number} year - The year to format (negative for BC, positive for AD)
 * @returns {string} Formatted year string
 */
export function formatYear(year) {
  if (year < 0) {
    return `${Math.abs(year)} BC`;
  }
  return `AD ${year}`;
}

/**
 * Format a full date with optional month and day.
 * @param {number} year - The year (negative for BC, positive for AD)
 * @param {number|null} [month] - Optional month (1-12)
 * @param {number|null} [day] - Optional day (1-31)
 * @returns {string} Formatted date string
 */
export function formatDate(year, month, day) {
  const yearStr = formatYear(year);

  if (!month) {
    return yearStr;
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const monthStr = monthNames[month - 1];

  if (!day) {
    return `${monthStr} ${yearStr}`;
  }

  return `${day} ${monthStr} ${yearStr}`;
}

/**
 * Get the primary year used for timeline positioning.
 * Returns datings[primaryDating].startYear if datings exist and primaryDating is a valid index,
 * otherwise returns event.startYear.
 * @param {object} event - The timeline event
 * @returns {number} The primary year for positioning
 */
export function getPrimaryYear(event) {
  if (
    event.datings &&
    Array.isArray(event.datings) &&
    event.datings.length > 0 &&
    typeof event.primaryDating === 'number' &&
    event.primaryDating >= 0 &&
    event.primaryDating < event.datings.length &&
    event.datings[event.primaryDating] &&
    typeof event.datings[event.primaryDating].startYear === 'number'
  ) {
    return event.datings[event.primaryDating].startYear;
  }
  return event.startYear;
}

/**
 * Calculate maximum visible depth based on the zoom range in years.
 * >500 → depth 1, ≤500 → depth 2, ≤100 → depth 3
 * @param {number} rangeYears - The number of years visible in the current zoom
 * @returns {number} Maximum depth level to display
 */
export function getMaxVisibleDepth(rangeYears) {
  if (rangeYears <= 100) {
    return 3;
  }
  if (rangeYears <= 500) {
    return 2;
  }
  return 1;
}

/**
 * Format a scripture reference object to a standard citation string.
 * @param {{ book: string, chapter: number, startVerse: number, endVerse?: number|null }} ref
 * @returns {string} Formatted citation, e.g. "Genesis 1:1" or "Genesis 1:1-3"
 */
export function formatScriptureRef(ref) {
  const base = `${ref.book} ${ref.chapter}:${ref.startVerse}`;
  if (ref.endVerse != null) {
    return `${base}-${ref.endVerse}`;
  }
  return base;
}
